"use client"

import * as React from "react"
import { UserButton, useUser } from "@clerk/clerk-react"
import { ArrowLeftIcon, BadgeCheckIcon, CompassIcon, DownloadIcon, LogOutIcon, PaletteIcon, SettingsIcon } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger, cn } from "@ibada/ui"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { MobileAccountScreen } from "@/features/auth/components/mobile-account-screen"

import { useTranslations } from "@/i18n"
import { AccountProfilePanel } from "@/features/auth"
import { useAppSignOut } from "@/features/auth/hooks/use-app-sign-out"
import { useRestartTour } from "@/features/onboarding"
import { DevicePushPreferencePanel } from "@/features/push"
import { ThemePreferencePanel } from "@/shared/theme/components/theme-preference-panel"
import { APP_VERSION_LABEL } from "@/shared/lib/app-version"
import { AppIconTile } from "@/shared/pwa/app-icon-tile"
import { IosInstallGuide } from "@/shared/pwa/ios-install-guide"
import { PwaInstallAvailability, PwaInstallOutcome, usePwaInstall } from "@/shared/pwa/pwa-install"

type TenantUserMenuContext = "personal" | "workspace" | "branch"

type TenantUserMenuProps = {
  context: TenantUserMenuContext
  afterSignOutUrl?: string
  backToWorkspaceHref?: string | null
  workspaceSettingsHref?: string | null
  currentBranchSettingsHref?: string | null
  className?: string
}

export function TenantUserMenu({
  context,
  afterSignOutUrl = "/login",
  backToWorkspaceHref,
  className,
}: TenantUserMenuProps) {
  const t = useTranslations("common.userMenu")
  const tTheme = useTranslations("settings.themeModeSwitcher")
  const navigate = useNavigate()
  const restartTour = useRestartTour()
  // Run the push cleanup (deactivate this device's server row + unsubscribe the browser) BEFORE Clerk tears down
  // the session — Clerk's built-in signOut action bypasses it, which left a shared browser's endpoint owned by
  // the previous user and blocked the next one from enabling notifications.
  const { signOut } = useAppSignOut({ redirectPath: afterSignOutUrl })
  const isMobile = useIsMobile()
  const install = useInstallApp()

  if (isMobile) {
    return (
      <MobileUserMenu
        className={className}
        backToWorkspaceHref={context === "personal" ? backToWorkspaceHref ?? null : null}
        install={install}
        onRestartTour={() => void restartTour()}
        onSignOut={() => void signOut()}
      />
    )
  }

  return (
    <div className={className} data-tour='user-menu'>
      <UserButton
        afterSignOutUrl={afterSignOutUrl}
        // Hide Clerk's built-in "Sign out" row — it's always appended and the reserved-label override isn't
        // honored in this Clerk version, so we render our own (below) that runs push cleanup first.
        appearance={{ elements: { userButtonPopoverActionButton__signOut: { display: "none" } } }}
      >
        {/* "Mon profil" now lives as a tab inside Manage account (no standalone /profile page). */}
        <UserButton.UserProfilePage
          label={t("profile")}
          labelIcon={<BadgeCheckIcon className='size-4' />}
          url='profile'
        >
          <AccountProfilePanel />
        </UserButton.UserProfilePage>

        {/* Preferences holds both appearance and notifications (notifications live inside Preferences). */}
        <UserButton.UserProfilePage
          label={tTheme("pageLabel")}
          labelIcon={<PaletteIcon className='size-4' />}
          url='preferences'
        >
          <div className='space-y-6'>
            <h1 className='text-xl font-semibold tracking-tight text-foreground'>{tTheme("pageLabel")}</h1>
            <div className='border-t border-border' />
            <ThemePreferencePanel embedded />
            <DevicePushPreferencePanel embedded />
          </div>
        </UserButton.UserProfilePage>

        <UserButton.MenuItems>
          {context === "personal" && backToWorkspaceHref ? (
            <UserButton.Action
              label={t("backToWorkspace")}
              labelIcon={<ArrowLeftIcon className='size-4' />}
              onClick={() => navigate(backToWorkspaceHref)}
            />
          ) : null}

          {/* Chrome and Edge open their own install window; an iPad shows the Share menu guide. */}
          {install.availability !== PwaInstallAvailability.Unavailable ? (
            <UserButton.Action label={install.menuLabel} labelIcon={<DownloadIcon className='size-4' />} onClick={install.start} />
          ) : null}

          <UserButton.Action
            label={t("restartTour")}
            labelIcon={<CompassIcon className='size-4' />}
            onClick={() => {
              void restartTour()
            }}
          />

          {/* Our sign-out: runs push cleanup while still authenticated (useAppSignOut deactivates this device +
              unsubscribes), then clerk.signOut. Clerk's own default sign-out is hidden via the appearance element
              above so only this one shows. */}
          <UserButton.Action
            label={t("logout")}
            labelIcon={<LogOutIcon className='size-4' />}
            onClick={() => void signOut()}
          />
          {/* Keep the version as the very last row, after Sign out. Clerk's menu only renders action
              items, so this is styled (globals.css, via the marker) as plain, centered, non-interactive
              text — no icon, no hover, no pointer, and it never closes the menu. */}
          <UserButton.Action
            label={`Version Ibada Cloud ${APP_VERSION_LABEL}`}
            labelIcon={<span className='app-version-menu-marker' aria-hidden />}
            onClick={() => undefined}
          />
        </UserButton.MenuItems>
      </UserButton>
      {install.guide}
    </div>
  )
}

/**
 * Installing the app as a home screen app: the browser's own install window where it has one (Android, desktop
 * Chrome and Edge), the Share menu guide on an iPhone, nothing inside the installed app or where neither exists.
 */
function useInstallApp() {
  const t = useTranslations("common.pwaInstall")
  const { availability, promptInstall } = usePwaInstall()
  const [guideOpen, setGuideOpen] = React.useState(false)
  const start = React.useCallback(() => {
    if (availability === PwaInstallAvailability.IosGuide) {
      setGuideOpen(true)
      return
    }
    void promptInstall().then((outcome) => {
      if (outcome === PwaInstallOutcome.Accepted) toast.success(t("installed"))
    })
  }, [availability, promptInstall, t])
  return {
    availability,
    start,
    menuLabel: t("menuLabel"),
    guide: <IosInstallGuide open={guideOpen} onOpenChange={setGuideOpen} />,
  }
}

/** The invitation to install, at the top of the phone account menu: the app icon, what it brings, an Install pill. */
function InstallAppCard({ onPress }: { onPress: () => void }) {
  const t = useTranslations("common.pwaInstall")
  return (
    <button
      type='button'
      onClick={onPress}
      className='flex w-full items-center gap-3 rounded-[1.25rem] bg-gradient-to-br from-primary/12 to-primary/5 p-3.5 text-left active:opacity-80'
    >
      <AppIconTile className='size-[2.875rem] rounded-xl' />
      <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <span className='line-clamp-2 text-[15px] leading-tight font-extrabold break-words text-foreground'>{t("cardTitle")}</span>
        <span className='truncate text-[13px] font-semibold text-muted-foreground'>{t("cardBody")}</span>
      </span>
      <span className='flex h-9 shrink-0 items-center rounded-full bg-primary px-4 text-sm font-extrabold text-primary-foreground'>{t("action")}</span>
    </button>
  )
}

/**
 * The account menu on a phone: the avatar opens the app's bottom sheet, headed by the person, with the actions
 * as rounded groups and sign-out in red on its own. "Manage account" is Clerk's own account screen, opened full
 * screen. Clerk still owns the account; only the menu around it is ours.
 */
function MobileUserMenu({
  className,
  backToWorkspaceHref,
  install,
  onRestartTour,
  onSignOut,
}: {
  className?: string
  backToWorkspaceHref: string | null
  install: ReturnType<typeof useInstallApp>
  onRestartTour: () => void
  onSignOut: () => void
}) {
  const t = useTranslations("common.userMenu")
  const navigate = useNavigate()
  const { user } = useUser()
  const [open, setOpen] = React.useState(false)
  const [accountOpen, setAccountOpen] = React.useState(false)
  const name = user?.fullName?.trim() || user?.primaryEmailAddress?.emailAddress || ""
  const email = user?.primaryEmailAddress?.emailAddress ?? ""
  const avatar = (size: string) =>
    user?.imageUrl ? (
      <img src={user.imageUrl} alt='' className={cn(size, "shrink-0 rounded-full object-cover")} />
    ) : (
      <span className={cn(size, "flex shrink-0 items-center justify-center rounded-full bg-primary/10 font-extrabold text-primary")}>
        {name.charAt(0).toUpperCase()}
      </span>
    )
  const row = (key: string, label: string, Icon: typeof SettingsIcon, onPress: () => void, destructive = false) => (
    <button
      key={key}
      type='button'
      onClick={() => {
        setOpen(false)
        onPress()
      }}
      className={cn(
        "flex min-h-[3.25rem] w-full items-center gap-3 px-4 text-left text-base font-semibold active:bg-muted",
        destructive ? "text-destructive" : "text-foreground",
      )}
    >
      <Icon aria-hidden className={cn("size-5 shrink-0", destructive ? "text-destructive" : "text-muted-foreground")} />
      <span className='min-w-0 flex-1 truncate'>{label}</span>
    </button>
  )

  return (
    <div className={className} data-tour='user-menu'>
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>
          <button type='button' aria-label={t("account")} className='rounded-full ring-2 ring-card'>
            {avatar("size-10")}
          </button>
        </DrawerTrigger>
        <DrawerContent>
          <DrawerHeader className='group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left'>
            <DrawerTitle className='sr-only'>{t("account")}</DrawerTitle>
            <DrawerDescription asChild>
              <div className='flex min-w-0 items-center gap-3'>
                {avatar("size-12")}
                <span className='flex min-w-0 flex-col gap-0.5'>
                  <span className='truncate text-[17px] font-extrabold text-foreground'>{name}</span>
                  {email && email !== name ? <span className='truncate text-sm font-semibold text-muted-foreground'>{email}</span> : null}
                </span>
              </div>
            </DrawerDescription>
          </DrawerHeader>
          <div className='flex flex-col gap-2.5 px-3 pb-[max(1rem,env(safe-area-inset-bottom))]'>
            {install.availability === PwaInstallAvailability.Unavailable ? null : (
              <InstallAppCard
                onPress={() => {
                  setOpen(false)
                  install.start()
                }}
              />
            )}
            <div className='divide-y divide-border/60 overflow-hidden rounded-2xl bg-muted/60'>
              {row("account", t("manageAccount"), SettingsIcon, () => setAccountOpen(true))}
              {backToWorkspaceHref ? row("back", t("backToWorkspace"), ArrowLeftIcon, () => navigate(backToWorkspaceHref)) : null}
              {row("tour", t("restartTour"), CompassIcon, onRestartTour)}
            </div>
            <div className='overflow-hidden rounded-2xl bg-muted/60'>{row("logout", t("logout"), LogOutIcon, onSignOut, true)}</div>
            <p className='pt-1 text-center text-xs font-semibold text-muted-foreground'>Ibada Cloud {APP_VERSION_LABEL}</p>
          </div>
        </DrawerContent>
      </Drawer>
      <MobileAccountScreen open={accountOpen} onOpenChange={setAccountOpen} />
      {install.guide}
    </div>
  )
}
